import "./cast.d.ts"
import type { JellyfinClient } from "./JellyfinClient.ts"
import type { ActiveSession } from "./types.ts"

const TAG = "PROGRESS"

// ────────────────────────────────────────────────────────────────────────────
// ProgressReporter — owns the Jellyfin watched-state lifecycle for exactly
// one session.  One instance per LOAD; replaced (and old one .stop()ed) on
// every queue transition.
//
// Lifecycle:
//   new → start()                  POST Sessions/Playing, begin 10s polling
//       → onPause() / onResume()   flip isPaused flag, flush one immediate
//                                  Progress POST so server state matches
//                                  within ~0s instead of ~10s.  Interval
//                                  KEEPS RUNNING during pause — keepalive
//                                  for Jellyfin's transcoding session.
//       → onSeek()                 opportunistic immediate Progress POST
//       → stop()                   clear interval, POST Sessions/Playing/Stopped
//
// Invariants:
//   - stop() is idempotent (survives the machine calling it twice during
//     teardownPriorSession + STOPPING_PROGRESS).
//   - sendProgress() no-ops after stop(), so a stray interval tick can't
//     post Progress for an ended session.
//   - Final Stopped position uses stop(finalPositionTicks) passed in by the
//     caller, which captures the player's true position at the moment the
//     session was decided to end.  Falls back to lastReportedTicks (up to ~10s
//     stale) then getPositionTicks() (may already reflect next queue item
//     during a LOAD-replace).
//   - This class never touches the state machine.  Main.ts subscribes to
//     machine transitions and calls onPause/onResume at the right moments.
//
// The 10s interval is the only timer in this codebase used for playback
// reporting — all UI/display updates are event-driven (see CLAUDE.md).
// ────────────────────────────────────────────────────────────────────────────

export class ProgressReporter {
  private intervalId: ReturnType<typeof setInterval> | null = null
  private _hasStarted = false
  private _hasStopped = false
  private isPaused = false
  private forceNextError = false
  /** Last position we actually reported to the server. Used by stop() so the
   * final "Stopped" report reflects the *old* item's position, not whatever
   * the player's currentTime has moved on to after a LOAD replace. */
  private lastReportedTicks: number | null = null

  private static readonly INTERVAL_MS = 10_000

  constructor(
    private readonly client: JellyfinClient,
    /** Returns current playback position in ticks (1 tick = 100ns). */
    private readonly getPositionTicks: () => number,
    readonly session: ActiveSession
  ) {}

  /** DEV: Force next progress report to fail (for testing error recovery). */
  setForceNextError(force: boolean): void {
    this.forceNextError = force
  }

  get hasStarted(): boolean {
    return this._hasStarted
  }

  async start(): Promise<void> {
    this._hasStarted = true
    this.isPaused = false
    try {
      await this.client.reportPlaybackStart({
        itemId: this.session.data.jellyfinItemID,
        mediaSourceId: this.session.data.mediaSourceID ?? "",
        playSessionId: this.session.playSessionId,
        audioStreamIndex: this.session.data.audioStreamIndex ?? 0,
        subtitleStreamIndex: this.session.data.subtitleStreamIndex,
        positionTicks: this.getPositionTicks(),
        canSeek: true,
        playMethod: this.session.data.playMethod ?? null,
      })
      this.lastReportedTicks = this.getPositionTicks()
      cast.debug.CastDebugLogger.getInstance().info(TAG, `playback started item=${this.session.data.jellyfinItemID} sess=${this.session.playSessionId}`)
    } catch (err) {
      cast.debug.CastDebugLogger.getInstance().error(TAG, `playback start failed: ${String(err)}`)
      throw err
    }
    this.schedulePolling()
  }

  onPause(): void {
    this.isPaused = true
    // Flush one progress POST immediately so Jellyfin sees the pause state
    // without waiting up to 10s for the next interval tick.
    this.sendProgress()
    // NOTE: do NOT clearPolling on pause.  Progress polling continues with
    // isPaused=true — this is the keepalive that stops Jellyfin from reaping
    // the HLS transcoding job during the pause (resets ffmpeg's 60s kill
    // timer per /Sessions/Playing/Progress matching PlaySessionId, see
    // docs/chromecast-jellyfin-receiver-reference.md §16).
  }

  onResume(): void {
    if (this._hasStopped) return
    this.isPaused = false
    // Same rationale as onPause: surface resume to server immediately.
    this.sendProgress()
    // Interval has been running throughout the pause (see onPause note) —
    // schedulePolling() here is a reset, not a start, and is harmless.
    this.schedulePolling()
  }

  onSeek(): void {
    this.sendProgress()
  }

  /**
   * @param finalPositionTicks caller-captured position at the moment stop was
   * decided.  Preferred over lastReportedTicks because the caller knows the
   * true end-of-session position — by the time this runs the player's
   * currentTime may already have advanced to the next queue item, and
   * lastReportedTicks can be up to ~10s stale.
   */
  async stop(finalPositionTicks?: number): Promise<void> {
    if (this._hasStopped) return
    this._hasStopped = true
    this.clearPolling()
    if (!this._hasStarted) return // nothing to tell server
    const posTicks = finalPositionTicks ?? this.lastReportedTicks ?? this.getPositionTicks()
    try {
      await this.client.reportPlaybackStopped({
        itemId: this.session.data.jellyfinItemID,
        mediaSourceId: this.session.data.mediaSourceID ?? "",
        playSessionId: this.session.playSessionId,
        positionTicks: posTicks,
      })
      cast.debug.CastDebugLogger.getInstance().info(TAG, `playback stopped item=${this.session.data.jellyfinItemID} ticks=${posTicks}`)
    } catch (err) {
      cast.debug.CastDebugLogger.getInstance().error(TAG, `playback stop failed: ${String(err)}`)
    }
  }

  private schedulePolling(): void {
    this.clearPolling()
    this.intervalId = setInterval(() => this.sendProgress(), ProgressReporter.INTERVAL_MS)
  }

  private clearPolling(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  }

  private sendProgress(): void {
    if (this._hasStopped) return // survive a stray interval tick after stop()
    const posTicks = this.getPositionTicks()
    this.lastReportedTicks = posTicks

    // Heap telemetry — non-standard performance.memory is exposed on
    // Chromium and thus on the Cast shell.  Helps catch slow OOM trajectories
    // that would otherwise only surface as a hard freeze.
    const mem = (performance as unknown as { memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number } }).memory
    if (mem) {
      const used = Math.round(mem.usedJSHeapSize / 1_048_576)
      const total = Math.round(mem.totalJSHeapSize / 1_048_576)
      const limit = Math.round(mem.jsHeapSizeLimit / 1_048_576)
      cast.debug.CastDebugLogger.getInstance().info(TAG, `[HEAP] used=${used}MB total=${total}MB limit=${limit}MB`)
    }

    // DEV: Force error for testing
    if (this.forceNextError) {
      this.forceNextError = false
      cast.debug.CastDebugLogger.getInstance().error(TAG, `progress report failed (DEV forced) ticks=${posTicks} item=${this.session.data.jellyfinItemID}`)
      return
    }

    this.client
      .reportPlaybackProgress({
        itemId: this.session.data.jellyfinItemID,
        mediaSourceId: this.session.data.mediaSourceID ?? "",
        playSessionId: this.session.playSessionId,
        audioStreamIndex: this.session.data.audioStreamIndex ?? 0,
        subtitleStreamIndex: this.session.data.subtitleStreamIndex,
        positionTicks: posTicks,
        isPaused: this.isPaused,
        isMuted: false,
        volumeLevel: 100,
        playMethod: this.session.data.playMethod ?? null,
      })
      .catch((err) => {
        cast.debug.CastDebugLogger.getInstance().error(TAG, `progress report failed: ${String(err)} ticks=${posTicks} item=${this.session.data.jellyfinItemID}`)
      })
  }
}
